import { cn } from '@listening/utils/react-helpers';
import { DEFAULT_AUDIO_TITLE } from '@listening/utils/ts-utils';
import CoverImgPlaceholderIcon from '@listening/assets/icons/cover-image-place-holder.svg?react';
import ErrorIcon from '@listening/assets/icons/error.svg?react';

import { Skeleton } from '@listening/frontend/components/radix/skeleton';
import { useState } from 'react';
import { Img } from 'react-image';
import { getDisplayAudioTitle } from '@/lib/audio-display-metadata';
import { Spinner } from '../spinner';
import { getBigBlurImageSrc } from './blur-thumbnail';

export function CoverImgWithBackgroundOld({
  imgSrc,
  className,
  imgClassName,
}: {
  imgSrc?: string | null | undefined;
  className?: string;
  imgClassName?: string;
}) {
  return (
    <div
      className={cn(
        'flex aspect-square w-full shrink-0 flex-col items-center overflow-clip bg-linear-to-r from-cyan-500 to-blue-500',
        className,
      )}
    >
      <Img
        // TODO: Pick a default
        className={cn('mt-4 w-5/6 rounded-md', imgClassName)}
        decode={false}
        loader={
          <Skeleton className='mt-4 h-full w-5/6 rounded-md rounded-b-none' />
        }
        src={imgSrc ?? ''}
      />
    </div>
  );
}

export function CoverImgWithBackgroundNew({
  coverImg,
  title,
  size,
  status,
  noRounding,
}: {
  coverImg?: string | null;
  title?: string | null;
  size?: 'sm' | 'lg';
  status?: 'processing' | 'error';
  noRounding?: boolean;
}) {
  const badSrc = !coverImg;
  const [loadOutcome, setLoadOutcome] = useState<{
    src: string;
    failed: boolean;
  } | null>(null);
  const outcome = loadOutcome?.src === coverImg ? loadOutcome : null;
  const isLoading = !badSrc && outcome === null;
  const isError = badSrc || outcome?.failed === true;
  const displayTitle = getDisplayAudioTitle(title);

  const imgClassName = cn(
    'absolute top-0 z-10 min-h-full w-full rounded-t-xl',
    size === 'sm' && !noRounding && 'rounded-sm',
    noRounding && 'rounded-sm',
  );

  return (
    <div
      className={cn(
        'relative flex size-full min-h-12 min-w-12 flex-col items-center overflow-hidden rounded-[20px] px-2 pt-3 pb-0',
        size === 'sm' && !noRounding && 'rounded-lg p-[2.5px] pb-0',
        noRounding && 'rounded-lg p-[2.5px] pb-0',
      )}
    >
      <img
        alt='Blur background'
        className='absolute top-0 size-[105%] dark:opacity-55'
        src={getBigBlurImageSrc(displayTitle ?? DEFAULT_AUDIO_TITLE)}
      />
      <div
        className={cn(
          'relative flex size-full flex-col items-center p-2 pb-0',
          size === 'sm' && 'p-[1.5px] pb-0',
        )}
      >
        {!(isLoading || isError) && (
          <div
            className={cn(
              'absolute top-0 size-full rounded-t-2xl bg-white opacity-50',
              size === 'sm' && 'rounded-t-md',
            )}
          />
        )}
        {status === 'processing' ? (
          <div
            className={cn(
              'flex size-full items-center justify-center',
              size === 'sm' ? 'pb-1' : 'pb-5',
            )}
          >
            <Spinner className='size-10 opacity-70' />
          </div>
        ) : status === 'error' ? (
          <div
            className={cn(
              'flex size-full items-center justify-center',
              size === 'sm' ? 'pb-1' : 'pb-5',
            )}
          >
            <ErrorIcon className='size-10' />
          </div>
        ) : (
          <div className='relative flex size-full flex-col items-center overflow-clip'>
            {isLoading && <Skeleton className={imgClassName} />}
            {isError ? (
              <CoverImgPlaceholderIcon
                className={cn(
                  'absolute size-24',
                  size === 'sm' ? 'mt-1 size-8' : 'mt-5',
                )}
              />
            ) : (
              <img
                alt='Audio Cover'
                className={imgClassName}
                onError={() => {
                  if (coverImg) setLoadOutcome({ src: coverImg, failed: true });
                }}
                onLoad={() => {
                  if (coverImg)
                    setLoadOutcome({ src: coverImg, failed: false });
                }}
                src={coverImg ?? ''}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
